// ============================================================
// Sparkline — une courbe en SVG, sans dépendance.
// ------------------------------------------------------------
// Une bibliothèque de graphiques coûte 40 à 150 Ko pour tracer une
// polyligne sur douze points. Ici il n'y a rien à interagir, rien à
// zoomer : on lit une tendance. Trente lignes de SVG suffisent, et
// elles restent lisibles par quelqu'un qui reprend le code — c'est la
// règle « pas de boîte noire » appliquée aussi aux dépendances.
//
// ── Deux règles de lecture, tenues par le composant ──
//
// **L'axe part de zéro.** Un axe tronqué transforme une baisse de 2 %
// en effondrement visuel. Sur un écran dont le seul rôle est de dire
// « est-ce que ça descend ? », c'est exactement le mensonge à ne pas
// faire.
//
// **Un seul point ne fait pas une courbe.** En dessous de deux mesures,
// on affiche la valeur en toutes lettres et on dit qu'il n'y a pas de
// tendance. Tracer une ligne sur un point donnerait l'image d'une
// tendance là où il n'y en a aucune.

import { formatPct } from '../../lib/format.js';

const W = 320;
const H = 72;
const PAD = 6;

export function Sparkline({ points = [], sens = 'descend' }) {
  const valides = points.filter((p) => p.y != null && Number.isFinite(Number(p.y)));

  if (valides.length === 0) {
    return <p className="field__hint">Pas encore de mesure.</p>;
  }
  if (valides.length === 1) {
    const seul = valides[0];
    return (
      <p className="field__hint">
        Une seule mesure — {seul.x} : <strong>{formatPct(seul.y)}</strong>. Il en faut
        deux pour parler de tendance.
      </p>
    );
  }

  // L'axe part de zéro, toujours. Voir l'en-tête.
  const max = Math.max(...valides.map((p) => Number(p.y)), 0) || 1;
  const pas = (W - 2 * PAD) / (valides.length - 1);
  const y = (v) => H - PAD - (Number(v) / max) * (H - 2 * PAD);
  const d = valides.map((p, i) => `${PAD + i * pas},${y(p.y)}`).join(' ');

  const premier = Number(valides[0].y);
  const dernier = Number(valides.at(-1).y);
  const bon = sens === 'descend' ? dernier < premier : dernier > premier;

  return (
    <div className="sparkline">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="sparkline__svg"
        role="img"
        aria-label={
          `De ${valides[0].x} à ${valides.at(-1).x} : ` +
          `${formatPct(premier)} puis ${formatPct(dernier)}. ` +
          (bon ? 'La tendance va dans le bon sens.' : 'La tendance ne va pas dans le bon sens.')
        }
      >
        <polyline
          points={d}
          fill="none"
          strokeWidth="2"
          className={bon ? 'sparkline__ligne sparkline__ligne--ok' : 'sparkline__ligne'}
        />
        {valides.map((p, i) => (
          <circle key={p.x} cx={PAD + i * pas} cy={y(p.y)} r="2.5" className="sparkline__point" />
        ))}
      </svg>
      <p className="field__hint">
        {valides[0].x} : {formatPct(premier)} → {valides.at(-1).x} : {formatPct(dernier)}
      </p>
    </div>
  );
}

export default Sparkline;
